import { bishopReach } from '@domain/chess/bishopReach';
import { squareShade } from '@domain/chess/fen';
import { allSquares, knightNeighbors } from '@domain/chess/knightReach';
import type { Square } from '@domain/chess/types';

export type BlindfoldPiece = 'n' | 'b';

export interface BlindfoldPuzzle {
  piece: BlindfoldPiece;
  from: Square;
  choices: readonly [Square, Square, Square];
  answer: Square;
}

type Rng = () => number;

function pick<T>(items: readonly T[], rng: Rng): T {
  const i = Math.min(items.length - 1, Math.floor(rng() * items.length));
  return items[i]!;
}

function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}

function offset(from: Square, df: number, dr: number): Square | null {
  const f = from.charCodeAt(0) - 97 + df;
  const r = Number(from[1]) - 1 + dr;
  if (f < 0 || f > 7 || r < 0 || r > 7) return null;
  return `${String.fromCharCode(97 + f)}${r + 1}` as Square;
}

export function reachable(piece: BlindfoldPiece, from: Square): Square[] {
  return piece === 'n' ? knightNeighbors(from) : bishopReach(from);
}

function temptingOffsets(piece: BlindfoldPiece): Array<readonly [number, number]> {
  if (piece === 'n') {
    return [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
      [2, 2],
      [2, -2],
      [-2, 2],
      [-2, -2],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [3, 1],
      [1, 3],
    ];
  }
  return [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [2, 1],
    [1, 2],
    [2, 0],
    [0, 2],
    [1, 2],
    [-1, 2],
  ];
}

function decoys(
  piece: BlindfoldPiece,
  from: Square,
  reach: ReadonlySet<Square>,
  rng: Rng,
): [Square, Square] {
  const forbidden = new Set<Square>([from, ...reach]);
  const tempting: Square[] = [];
  for (const [df, dr] of temptingOffsets(piece)) {
    const sq = offset(from, df, dr);
    if (sq && !forbidden.has(sq) && !tempting.includes(sq)) tempting.push(sq);
  }
  const rest = shuffle(
    allSquares().filter((s) => !forbidden.has(s) && !tempting.includes(s)),
    rng,
  );
  const pool = [...shuffle(tempting, rng), ...rest];
  const a = pool[0];
  const b = pool[1];
  if (!a || !b) throw new Error('Could not pick blindfold decoys');
  return [a, b];
}

export function pieceNotation(puzzle: BlindfoldPuzzle): string {
  return `${puzzle.piece === 'n' ? 'N' : 'B'}${puzzle.from}`;
}

export function shadeLabel(square: Square): 'Light' | 'Dark' {
  return squareShade(square) === 'dark' ? 'Dark' : 'Light';
}

/** After a guess: the answer square, present tense if correct. */
export function feedbackLine(puzzle: BlindfoldPuzzle, picked: Square): string {
  const label = `${shadeLabel(puzzle.answer)} ${puzzle.answer}`;
  return picked === puzzle.answer ? `${label} is right` : `${label} was right`;
}

export function nextBlindfoldPuzzle(
  rng: Rng = Math.random,
  opts?: { exclude?: { piece: BlindfoldPiece; from: Square } },
): BlindfoldPuzzle {
  for (let i = 0; i < 80; i++) {
    const piece: BlindfoldPiece = rng() < 0.5 ? 'n' : 'b';
    const from = pick(allSquares(), rng);
    if (opts?.exclude && opts.exclude.piece === piece && opts.exclude.from === from) {
      continue;
    }
    const reach = reachable(piece, from);
    if (reach.length === 0) continue;
    const answer = pick(reach, rng);
    const [d1, d2] = decoys(piece, from, new Set(reach), rng);
    const choices = shuffle([answer, d1, d2], rng) as [Square, Square, Square];
    return { piece, from, choices, answer };
  }
  throw new Error('Could not generate a blindfold puzzle');
}
